import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RegistrationStage, SmsRegistrationService } from '@galaxy/sms';
import { combineLatest, map, Observable } from 'rxjs';
import { InboxService } from '../inbox.service';
import { ConversationDetail } from '../interface/conversation.interface';
import { CONVERSATION_COUNTRY_TOKEN, CONVERSATION_SMS_ENABLED_TOKEN } from '../tokens';
import { AlertOptions, ConversationChannelService } from './conversation-channel.abstract';

@Injectable()
export class ConversationSMSService extends ConversationChannelService {
  private router = inject(Router);
  private readonly smsRegistrationService = inject(SmsRegistrationService);
  private readonly inboxService = inject(InboxService);
  private readonly country$ = inject(CONVERSATION_COUNTRY_TOKEN);
  private readonly smsEnabled$ = inject(CONVERSATION_SMS_ENABLED_TOKEN);

  getChatComposerAlert(
    partnerId: string,
    accountGroupId: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    conversationDetail?: ConversationDetail,
  ): Observable<AlertOptions | null> {
    const stage$ = this.smsRegistrationService.registrationStage$;
    const SMSNumber$ = this.inboxService.SMSNumber$;
    return combineLatest([this.country$, this.smsEnabled$, stage$, SMSNumber$]).pipe(
      map(([accountGroupCountry, smsEnabled, stage, smsNumber]) => {
        const country = accountGroupCountry?.toLowerCase() || '';
        if (!smsEnabled) {
          return this.getSMSMessageEnabledError();
        }
        if (!smsNumber) {
          return this.getSMSMPhoneNumberError();
        }
        if (country != 'us' && smsNumber) {
          return null;
        }
        return stage !== RegistrationStage.RegistrationComplete ? this.getSMSRegistrationError(accountGroupId) : null;
      }),
    );
  }

  private getSMSRegistrationError(accountGroupId: string): AlertOptions {
    return <AlertOptions>{
      title: 'INBOX.SETTINGS.SMS_MESSAGES.COMPOSER_A2P_REGISTRATION_REQUIRED.TITLE',
      description: 'INBOX.SETTINGS.SMS_MESSAGES.COMPOSER_A2P_REGISTRATION_REQUIRED.DESCRIPTION',
      action: {
        title: 'INBOX.SETTINGS.SMS_MESSAGES.COMPOSER_A2P_REGISTRATION_REQUIRED.ACTION_TITLE',
        callback: () => this.router.navigate([`/account/location/${accountGroupId}/settings/inbox/sms-registration`]),
      },
    };
  }

  private getSMSMessageEnabledError(): AlertOptions {
    return <AlertOptions>{
      title: 'INBOX.SETTINGS.SMS_MESSAGES.NOT_ENABLED.TITLE',
      description: 'INBOX.SETTINGS.SMS_MESSAGES.NOT_ENABLED.DESCRIPTION',
    };
  }

  private getSMSMPhoneNumberError(): AlertOptions {
    return <AlertOptions>{
      title: 'INBOX.SETTINGS.SMS_MESSAGES.NO_PHONE_NUMBER.TITLE',
      description: 'INBOX.SETTINGS.SMS_MESSAGES.NO_PHONE_NUMBER.DESCRIPTION',
    };
  }
}
